import React from "react";
import { X } from "lucide-react";

type FeeBreakdown = {
  amount: number;
  provider_fee: number;
  platform_fee: number;
  total_fee: number;
  total_debit: number;
};

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  progress: number; // Percentage of progress (0 to 100)
  status: "processing" | "completed" | "failed"; // status of the withdrawal
  message?: string;
  pinModal: (open: boolean) => void;
  feeBreakdown?: FeeBreakdown | null;
};

const ProgressModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  progress,
  status,
  message,
  pinModal,
  feeBreakdown,
}) => {
  if (!isOpen) return null;

  // Calculate circle progress
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (progress / 100) * circumference;

  // Render different content based on the status
  const renderModalContent = () => {
    switch (status) {
      case "processing":
        return (
          <div className="border-2 border-dashed border-fleetblue rounded-lg p-12 bg-[#F5F6FA]">
            <div className="flex flex-col items-center justify-center">
              {/* Circular Progress */}
              <div className="relative w-24 h-24 mb-6">
                <svg className="transform -rotate-90 w-24 h-24">
                  <circle
                    cx="48"
                    cy="48"
                    r="45"
                    stroke="#E5E7EB"
                    strokeWidth="6"
                    fill="none"
                  />
                  <circle
                    cx="48"
                    cy="48"
                    r="45"
                    stroke="#3B82F6"
                    strokeWidth="6"
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={strokeDashoffset}
                    strokeLinecap="round"
                    className="transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-blue-600 font-semibold text-lg">
                    {progress}%
                  </span>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Processing...
              </h3>
              <p className="text-gray-600 text-sm">
                {progress < 50
                  ? "Please wait, this will take some seconds."
                  : "Please wait, just a little more seconds."}
              </p>
            </div>
          </div>
        );
      case "completed":
        return (
          <div className="border-2 border-dashed border-[#26A65D] rounded-lg p-12 bg-[#EAFAF1]">
            <div className="flex flex-col items-center justify-center">
              {/* Success Circle */}
              <div className="relative w-24 h-24 mb-6">
                <svg className="w-24 h-24">
                  <circle
                    cx="48"
                    cy="48"
                    r="45"
                    stroke="#10B981"
                    strokeWidth="6"
                    fill="none"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-green-600 font-semibold text-lg">
                    100%
                  </span>
                </div>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Done!
              </h3>
              <p className="text-gray-600 text-sm mb-6">
                Transaction completed
              </p>

              {feeBreakdown && (
                <div className="w-full bg-white border border-green-200 rounded-lg p-4 mb-6 text-sm text-left">
                  <div className="flex justify-between font-semibold text-gray-800">
                    <span>Total debited (incl. fees)</span>
                    <span>₦{feeBreakdown.total_debit.toLocaleString()}</span>
                  </div>
                </div>
              )}

              <button
                onClick={onClose}
                className="bg-white border border-green-600 text-green-600 py-2 px-6 rounded-lg hover:bg-green-50 transition-colors"
              >
                Return to Wallet
              </button>
            </div>
          </div>
        );
      case "failed":
        return (
          <div className="border-2 border-dashed border-[#9A0023] rounded-lg p-12 bg-[#FEFAFA]">
            <div className="flex flex-col items-center justify-center">
              {/* Error Circle */}
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-6">
                <svg
                  className="w-8 h-8 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-semibold text-gray-800 mb-2">
                Transaction Failed
              </h3>
              <p className="text-gray-600 text-sm text-center mb-6">
                {message || (
                  <p>
                    "We couldn't process your payment. Please check your
                    <br />
                    card or try another method.
                  </p>
                )}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={pinModal.bind(null, true)}
                  className="bg-blue-600 text-white py-2 px-6 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="bg-white border border-gray-300 text-gray-700 py-2 px-6 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {/* Contact Support */}
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-xl shadow-xl p-8 max-w-lg w-full mx-4">
        {renderModalContent()}
      </div>
    </div>
  );
};

export default ProgressModal;
